import {
  type AutoAssignInput,
  type AutoAssignResult,
  type StoryPointEstimate,
  type StoryPointEstimateInput,
  type TaskTriage,
  type TaskTriageInput,
  type AiSuggestionView,
  type CreateTaskInput,
  type ProjectMemberView,
  type ProjectStoryPointRates,
  type ProjectTaskType,
  type RecurrenceInput,
  type RecurrenceView,
  type TaskMutationResult,
  type TaskView,
  type EstimateByReferenceInput,
  type EstimateReferenceCandidates,
  type EstimateCalibration,
  type UpdateTaskInput,
  type WorkStandards,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Tạo / sửa task, và nút "AI tách giúp" — APM-1308b.
 *
 * `boardApi` giữ những thao tác **kéo thả** (đổi cột, đổi thứ tự, đổi sprint);
 * ở đây là những thứ người dùng **gõ**. Tách ra vì hai nhóm có vòng đời khác
 * nhau: board gọi liên tục và tối ưu cho phản hồi tức thì, form gọi một lần khi
 * bấm lưu và trả kèm `warnings[]` phải hiện ra.
 */
export const taskFormApi = {
  create: (projectId: string, body: CreateTaskInput) =>
    apiFetch<TaskMutationResult>(`/projects/${projectId}/tasks`, { method: 'POST', body }),

  update: (taskId: string, body: UpdateTaskInput) =>
    apiFetch<TaskMutationResult>(`/tasks/${taskId}`, { method: 'PATCH', body }),

  /** Việc mẫu cho popup ước lượng — APM-EREF-b. */
  estimateReferences: (taskId: string) =>
    apiFetch<EstimateReferenceCandidates>(`/tasks/${taskId}/estimate-references`),

  /** Người gọi hay lệch thế nào khi ước lượng theo mẫu — APM-EREF-d. */
  estimateCalibration: () => apiFetch<EstimateCalibration>('/me/estimate-calibration'),

  /** Ước lượng theo việc mẫu — APM-EREF-b. */
  estimateByReference: (taskId: string, body: EstimateByReferenceInput) =>
    apiFetch<{
      result: { estimateHours: number; storyPoints: number | null; needsReview: boolean };
      task: TaskView;
    }>(`/tasks/${taskId}/estimate-by-reference`, { method: 'POST', body }),


  /** Gợi ý lúc đang gõ — APM-AI-h. `deterministicOnly` là nhánh không tốn tiền. */
  triage: (projectId: string, body: TaskTriageInput) =>
    apiFetch<TaskTriage>(`/projects/${projectId}/tasks/triage`, { method: 'POST', body }),

  /** Quy chuẩn để cảnh báo **ngay lúc gõ** — cùng con số API dùng lúc lưu. */
  standards: (projectId: string) =>
    apiFetch<WorkStandards>(`/projects/${projectId}/work-standards`),

  taskTypes: (projectId: string) =>
    apiFetch<ProjectTaskType[]>(`/projects/${projectId}/task-types`),

  members: (projectId: string) => apiFetch<ProjectMemberView[]>(`/projects/${projectId}/members`),
  /** Giờ cho 1 SP của từng thành viên — form tự tính giờ từ SP (2026-11-02). */
  storyPointRates: (projectId: string) =>
    apiFetch<ProjectStoryPointRates>(`/projects/${projectId}/story-point-rates`),

  /**
   * Đổi người làm của một việc **đã có** — APM-TASK-assign.
   *
   * Đường riêng chứ không gộp vào `update`: nó chạy phép kiểm quá tải và trả
   * `warnings[]`, nên gộp vào form sửa thì một cú bấm "Lưu" làm hai việc có hệ
   * quả khác nhau — và cảnh báo quá tải sẽ lẫn vào cảnh báo của phần còn lại.
   */
  assign: (taskId: string, assigneeId: string | null) =>
    apiFetch<TaskMutationResult>(`/tasks/${taskId}/assignee`, {
      method: 'PATCH',
      body: { assigneeId },
    }),

  /**
   * Nút "Estimate Storypoint" — APM-AIA-a. Không có key AI vẫn trả con số
   * (trung vị việc tương tự); việc chưa đủ chi tiết ⇒ 400 `TASK_NOT_DETAILED_ENOUGH`.
   */
  estimatePoints: (projectId: string, body: StoryPointEstimateInput) =>
    apiFetch<StoryPointEstimate>(`/projects/${projectId}/tasks/estimate-points`, {
      method: 'POST',
      body,
    }),

  /** Nút "Auto Assign" — APM-AIA-c. Chỉ gợi ý: không ghi gì vào task. */
  autoAssign: (projectId: string, body: AutoAssignInput) =>
    apiFetch<AutoAssignResult>(`/projects/${projectId}/tasks/auto-assign`, {
      method: 'POST',
      body,
    }),

  /** Chạy skill qua cửa chung — chỗ duy nhất kiểm hạn mức và ghi usage (ADR-006). */
  askBreakdown: (projectId: string, taskId: string, prompt: string, runId?: string) =>
    apiFetch<AiSuggestionView>('/ai/skills/task_breakdown/run', {
      method: 'POST',
      body: { projectId, prompt, params: { taskId }, ...(runId ? { runId } : {}) },
    }),

  acceptBreakdown: (taskId: string, suggestionId: string, indexes: number[]) =>
    apiFetch<TaskView[]>(`/tasks/${taskId}/breakdown-suggestions/${suggestionId}/accept`, {
      method: 'POST',
      body: { indexes },
    }),
};

export const taskFormKeys = {
  standards: (projectId: string) => ['projects', projectId, 'work-standards'] as const,
  taskTypes: (projectId: string) => ['projects', projectId, 'task-types'] as const,
  members: (projectId: string) => ['projects', projectId, 'member-list'] as const,
  storyPointRates: (projectId: string) => ['projects', projectId, 'story-point-rates'] as const,
};

/**
 * Việc định kỳ của dự án bảo trì — APM-REC-b. Mọi lệnh ghi trả **cả danh sách**
 * mới (giống API), nên chỗ gọi chỉ việc đặt lại cache, không phải tự vá.
 */
export const recurrenceApi = {
  list: (projectId: string) => apiFetch<RecurrenceView[]>(`/projects/${projectId}/recurrences`),

  create: (projectId: string, body: RecurrenceInput) =>
    apiFetch<RecurrenceView[]>(`/projects/${projectId}/recurrences`, { method: 'POST', body }),

  update: (projectId: string, id: string, body: RecurrenceInput) =>
    apiFetch<RecurrenceView[]>(`/projects/${projectId}/recurrences/${id}`, {
      method: 'PATCH',
      body,
    }),

  setActive: (projectId: string, id: string, isActive: boolean) =>
    apiFetch<RecurrenceView[]>(`/projects/${projectId}/recurrences/${id}/active`, {
      method: 'PATCH',
      body: { isActive },
    }),

  remove: (projectId: string, id: string) =>
    apiFetch<RecurrenceView[]>(`/projects/${projectId}/recurrences/${id}`, { method: 'DELETE' }),
};

export const recurrenceKeys = {
  list: (projectId: string) => ['projects', projectId, 'recurrences'] as const,
};
